/**
 * TilePalette - Draggable dialog for selecting tiles to paint on the sub-hex map.
 * Shows all available tiles organized by domain.
 */

import { useState, useRef, useCallback, useEffect } from 'react';
import { listTilesByDomain, type Domain } from '@/components/HexMap/tiles';
import type { SelectedTile } from './types';

interface TilePaletteProps {
    selectedTile: SelectedTile | null;
    onSelectTile: (tile: SelectedTile | null) => void;
    onClose: () => void;
}

// Available domains
const DOMAINS: { id: Domain; label: string; emoji: string }[] = [
    { id: 'greenlands', label: 'Greenlands', emoji: '🌲' },
    { id: 'drylands', label: 'Drylands', emoji: '🏜️' },
    { id: 'icelands', label: 'Icelands', emoji: '❄️' },
    { id: 'sandlands', label: 'Sandlands', emoji: '🏖️' },
    { id: 'deadlands', label: 'Deadlands', emoji: '💀' },
];

// Wood panel styling to match SubHexMap toolbars
const woodPanelStyle: React.CSSProperties = {
    background: 'linear-gradient(180deg, #6B4423 0%, #5D3A1A 50%, #4A2D14 100%)',
    border: '2px solid #C9A227',
    boxShadow: '0 4px 12px rgba(0,0,0,0.5)',
};

const woodHeaderStyle: React.CSSProperties = {
    background: 'linear-gradient(180deg, #8B6914 0%, #6B4423 100%)',
    borderBottom: '1px solid #C9A227',
};

export function TilePalette({ selectedTile, onSelectTile, onClose }: TilePaletteProps) {
    // Current domain tab
    const [activeDomain, setActiveDomain] = useState<Domain>('greenlands');

    // Dragging state
    const [position, setPosition] = useState({ x: 20, y: 80 });
    const [isDragging, setIsDragging] = useState(false);
    const dragOffset = useRef({ x: 0, y: 0 });
    const dialogRef = useRef<HTMLDivElement>(null);

    // Get tiles for current domain
    const tiles = listTilesByDomain(activeDomain);

    // Handle drag start
    const handleMouseDown = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
        // Only start drag from the header
        if ((e.target as HTMLElement).closest('.palette-header')) {
            setIsDragging(true);
            dragOffset.current = {
                x: e.clientX - position.x,
                y: e.clientY - position.y,
            };
        }
    }, [position]);

    // Handle drag move
    useEffect(() => {
        if (!isDragging) return;

        const handleMouseMove = (e: MouseEvent) => {
            setPosition({
                x: e.clientX - dragOffset.current.x,
                y: e.clientY - dragOffset.current.y,
            });
        };

        const handleMouseUp = () => {
            setIsDragging(false);
        };

        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('mouseup', handleMouseUp);

        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isDragging]);

    // Handle tile selection
    const handleTileClick = useCallback((filename: string, url: string) => {
        onSelectTile({
            domain: activeDomain,
            tileImage: filename,
            url,
        });
    }, [activeDomain, onSelectTile]);

    return (
        <div
            ref={dialogRef}
            className="fixed z-50 rounded-lg shadow-2xl w-80"
            style={{
                ...woodPanelStyle,
                left: position.x,
                top: position.y,
                cursor: isDragging ? 'grabbing' : 'default',
            }}
            onMouseDown={handleMouseDown}
        >
            {/* Header - draggable */}
            <div
                className="palette-header flex items-center justify-between px-4 py-3 rounded-t-lg cursor-grab"
                style={woodHeaderStyle}
            >
                <h3 className="text-amber-100 font-medium flex items-center gap-2">
                    🎨 Tile Palette
                </h3>
                <button
                    onClick={onClose}
                    className="text-amber-300 hover:text-white transition-colors"
                    title="Close palette"
                >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>
            </div>

            {/* Domain tabs */}
            <div className="flex border-b border-amber-700/50 overflow-x-auto">
                {DOMAINS.map(domain => (
                    <button
                        key={domain.id}
                        onClick={() => setActiveDomain(domain.id)}
                        className={`flex-1 min-w-0 px-2 py-2 text-xs font-medium transition-colors ${
                            activeDomain === domain.id
                                ? 'bg-amber-900/50 text-amber-100 border-b-2 border-amber-500'
                                : 'text-amber-300 hover:text-white hover:bg-amber-900/30'
                        }`}
                        title={domain.label}
                    >
                        <span className="block">{domain.emoji}</span>
                    </button>
                ))}
            </div>

            {/* Tile grid */}
            <div className="p-3 max-h-80 overflow-y-auto">
                <div className="grid grid-cols-4 gap-2">
                    {tiles.map(tile => {
                        const isSelected = selectedTile?.domain === activeDomain && selectedTile?.tileImage === tile.filename;
                        return (
                            <button
                                key={tile.filename}
                                onClick={() => handleTileClick(tile.filename, tile.url)}
                                className={`relative aspect-square rounded-lg overflow-hidden border-2 transition-all ${
                                    isSelected
                                        ? 'border-amber-400 ring-2 ring-amber-400/50'
                                        : 'border-amber-800 hover:border-amber-600'
                                }`}
                                title={tile.filename.replace(/^\d+-[a-z]+_/, '').replace(/\.[^.]+$/, '').replace(/-/g, ' ')}
                            >
                                <img
                                    src={tile.url}
                                    alt={tile.filename}
                                    className="w-full h-full object-cover"
                                    loading="lazy"
                                />
                                {isSelected && (
                                    <div className="absolute inset-0 bg-amber-500/20 flex items-center justify-center">
                                        <span className="text-white text-lg">✓</span>
                                    </div>
                                )}
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Footer with hint */}
            <div
                className="px-4 py-2 rounded-b-lg border-t border-amber-700/50"
                style={{ background: 'rgba(0,0,0,0.2)' }}
            >
                <p className="text-xs text-amber-300 text-center">
                    Select a tile, then click on the map to paint
                </p>
            </div>
        </div>
    );
}

